import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Container,
  Typography,
  Grid,
  Link,
  Box,
  Stack,
  Divider,
} from '@mui/material';
import { Email, LinkedIn, Twitter, Favorite } from '@mui/icons-material';

function Footer() {
  const [mounted, setMounted] = useState(false);
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const logoVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  if (!mounted) return null;

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, var(--color-neutral-900), var(--color-neutral-800))',
        color: 'white',
        py: 8,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'radial-gradient(circle at 20% 20%, rgba(255, 49, 48, 0.1) 0%, transparent 50%), ' +
            'radial-gradient(circle at 80% 80%, rgba(255, 224, 92, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          <Grid container spacing={6}>
            <Grid item xs={12} md={8}>
              <motion.div variants={logoVariants}>
                <Stack spacing={3}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, var(--color-brand-primary), var(--color-accent-primary))',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1,
                      fontSize: '2.5rem',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    Kazi AI
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'var(--color-neutral-300)',
                      lineHeight: 1.7,
                      fontSize: '1.125rem',
                      maxWidth: '500px',
                    }}
                  >
                    Your career story deserves to be understood—and told brilliantly. We're here to help you do both.
                  </Typography>
                </Stack>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={4}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1.25rem',
                  }}
                >
                  Legal
                </Typography>
                <Stack spacing={2}>
                  <Link
                    href="/privacy-policy.html"
                    target="_blank"
                    component={motion.a}
                    whileHover={{
                      x: 10,
                      color: 'var(--color-brand-light)',
                      transition: { duration: 0.2 }
                    }}
                    sx={{
                      color: 'var(--color-neutral-300)',
                      textDecoration: 'none',
                      fontSize: '1rem',
                      fontWeight: 500,
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      '&:hover': {
                        color: 'var(--color-brand-light)',
                      },
                    }}
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    href="/terms-of-service.html"
                    target="_blank"
                    component={motion.a}
                    whileHover={{
                      x: 10,
                      color: 'var(--color-brand-light)',
                      transition: { duration: 0.2 }
                    }}
                    sx={{
                      color: 'var(--color-neutral-300)',
                      textDecoration: 'none',
                      fontSize: '1rem',
                      fontWeight: 500,
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      '&:hover': {
                        color: 'var(--color-brand-light)',
                      },
                    }}
                  >
                    Terms of Service
                  </Link>
                </Stack>
              </motion.div>
            </Grid>
          </Grid>

          <motion.div variants={itemVariants}>
            <Divider
              sx={{
                my: 6,
                borderColor: 'rgba(255, 255, 255, 0.1)',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 60,
                  height: 1,
                  background: 'linear-gradient(90deg, var(--color-brand-primary), var(--color-accent-primary))',
                },
              }}
            />
          </motion.div>

          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center' }}>
              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                <Typography
                  variant="body2"
                  sx={{
                    color: 'var(--color-neutral-400)',
                    fontSize: '0.95rem',
                  }}
                >
                  © 2025 Kazi AI. All rights reserved.
                </Typography>
              </Stack>

              {/* Additional animated elements */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                  {[...Array(3)].map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: i === 1
                          ? 'var(--color-brand-primary)'
                          : 'var(--color-accent-primary)',
                        animation: 'pulse 2s infinite',
                        animationDelay: `${i * 0.3}s`,
                        opacity: 0.6,
                      }}
                    />
                  ))}
                </Box>
              </motion.div>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
}

export default Footer; 